// ShowcasePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import Navbar from "../components/Navbar.jsx";
import Footer from "../components/Footer.jsx";
import { useAuth } from "../features/auth";
import { useVideoModal } from "../components/VideoModalContext";
import { useProjects } from "../features/catalog";
import ImageWithFallback from "../components/ImageWithFallback";
import { projectPageUrl } from "../lib/projectUrl";

import yt1 from "../assets/logos/yt1.png";
import vz1 from "../assets/logos/vz1.png";
import searchIcon from "../assets/icons/search.png";
import demoIcon from "../assets/icons/view-demo.png";
import arrowPng from "../assets/icons/arrow.png";

import indiaIcon from "../assets/icons/flag-in.png";
import usIcon from "../assets/icons/flag-us.png";

import "../features/catalog/catalog.css";

function getRegionFromPath(pathname = "") {
  const parts = pathname.split("/").filter(Boolean);
  const first = (parts[0] || "").toLowerCase();
  return first === "us" ? "us" : "in";
}

const norm = (s = "") =>
  String(s).toLowerCase().replace(/_/g, " ").replace(/\s+/g, " ").trim();

function formatSqft(n = "") {
  const s = String(n).replace(/,/g, "").trim();
  const val = Number(s);
  if (Number.isFinite(val) && val > 0) return `${val.toLocaleString()} sqft`;
  return n || "";
}

function normalizeServer(v = "") {
  const s = norm(v);
  if (s.includes("us")) return "us";
  if (s.includes("india") || s === "in") return "india";
  return "";
}

/** ====== FEATURED CARD ====== */
function FeaturedCard({
  item,
  onOpenProject,
  onOpenVizdom,
  onOpenYoutube,
  onOpenDemo,
}) {
  const hasYoutube = Boolean(String(item.youtube || "").trim());
  const hasVizdom = Boolean(String(item.vizdomId || "").trim());
  const hasDemo = Boolean(String(item.demoLink || "").trim());

  const openDemo = (e) => {
    e.stopPropagation();
    onOpenDemo?.(item);
  };

  const openYoutube = (e) => {
    e.stopPropagation();
    onOpenYoutube?.(item);
  };

  return (
    <article className="fpProjectCard">
      <div className="fpCardMedia">
        <ImageWithFallback
          className="fpCardImg"
          src={item.thumb}
          alt={item.name}
        />

        <button
          className="fpViewPill"
          onClick={(e) => {
            e.stopPropagation();
            onOpenProject(item);
          }}
          type="button"
        >
          View Project
          <img
            className="fpViewPillArrow"
            src={arrowPng}
            alt=""
            aria-hidden="true"
          />
        </button>
      </div>

      <div className="fpCardDetails">
        <h3 className="fpProjectName">{item.name || "Project"}</h3>

        <p className="fpProjectMeta">
          {(item.constructionType || item.industry || "—")} |{" "}
          {formatSqft(item.areaSqft || "")}
        </p>

        <div className="fpCardFooter">
          {hasYoutube ? (
            <button
              className="fpFooterSquare"
              onClick={openYoutube}
              type="button"
              aria-label="Open YouTube"
            >
              <img src={yt1} alt="" className="fpFooterSquareImg fpYtImg" />
            </button>
          ) : null}

          {hasVizdom ? (
            <button
              className="fpFooterSquare"
              onClick={(e) => {
                e.stopPropagation();
                onOpenVizdom(item);
              }}
              type="button"
              aria-label="Open Vizdom"
            >
              <img src={vz1} alt="" className="fpFooterSquareImg" />
            </button>
          ) : null}

          {hasDemo ? (
            <button className="fpFooterDemoBtn" onClick={openDemo} type="button">
              <img src={demoIcon} alt="" className="fpDemoIcon" />
              <span>View Demo</span>
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}

/** ====== SHOWCASE PAGE ====== */
export default function ShowcasePage() {
  const { user, signOut } = useAuth();
  const { openVideo } = useVideoModal();
  const location = useLocation();
  const currentRegion = getRegionFromPath(location.pathname);

  const { projects, loading } = useProjects("Showcase");

  const [selectedServer, setSelectedServer] = useState(
    currentRegion === "us" ? "us" : "india"
  );
  const [activeCategory2, setActiveCategory2] = useState("All");
  const [searchQuery2, setSearchQuery2] = useState("");

  useEffect(() => {
    setSelectedServer(currentRegion === "us" ? "us" : "india");
  }, [currentRegion]);

  useEffect(() => {
    setActiveCategory2("All");
    setSearchQuery2("");
  }, [selectedServer]);

  // Adapts the unified `Project` shape (src/features/catalog/useProjects.js) to the local card
  // shape this page has always rendered, so nothing below this needs to change.
  const items = useMemo(
    () =>
      projects.map((p) => ({
        id: p.id,
        server: normalizeServer(p.server),
        sbu: p.sbu,
        name: p.name,
        buildLabel: p.buildLabel,
        areaSqft: p.areaSqft ?? "",
        industry: p.industry,
        designStyle: p.designStyle,
        thumb: p.thumbnail,
        youtube: p.media.find((m) => m.kind === "walkthrough")?.urls[0] || "",
        vizdomId: p.vizdomId,
        constructionType: p.constructionType,
        demoLink: p.media.find((m) => m.kind === "demo")?.urls[0] || "",
      })),
    [projects]
  );

  const typeOptions = useMemo(() => {
    const set = new Set(["All"]);
    items.forEach((it) => {
      const t = String(it.constructionType || "").trim();
      if (t) set.add(t);
    });
    return Array.from(set);
  }, [items]);

  const filtered = useMemo(() => {
    const q = norm(searchQuery2);

    return items.filter((it) => {
      if (selectedServer && it.server && it.server !== selectedServer) return false;
      if (selectedServer && !it.server) return false;

      if (activeCategory2 !== "All") {
        const typeOk = String(it.constructionType || "").trim() === activeCategory2;
        if (!typeOk) return false;
      }

      if (!q) return true;
      const big = `${it.name} ${it.buildLabel} ${it.areaSqft} ${it.industry} ${it.designStyle} ${it.sbu}`;
      return norm(big).includes(q);
    });
  }, [items, selectedServer, activeCategory2, searchQuery2]);

  const handleOpenProject = (item) => {
    sessionStorage.setItem(
      "SG_BACK_URL",
      `${window.location.pathname}${window.location.search}${window.location.hash}`
    );

    window.location.assign(projectPageUrl(currentRegion, item.id));
  };

  const handleOpenVizdom = (item) => {
    const id = String(item?.vizdomId || "").trim();
    if (!id) return;
    const url = `https://vizdom.flipspaces.app/user/project/${encodeURIComponent(id)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleOpenYoutube = (item) => {
    openVideo(
      item.youtube,
      item.name || "Project Walkthrough",
      "Offline-ready project walkthrough",
      { type: "youtube" }
    );
  };

  const handleOpenDemo = (item) => {
    openVideo(
      item.demoLink,
      item.name || "Project Demo",
      "Offline-ready project Demo",
      { type: "demo" }
    );
  };

  return (
    <div style={sx.page}>
      <Navbar
        user={user}
        signOut={signOut}
        selectedServer={selectedServer}
        setSelectedServer={setSelectedServer}
      />

      <section className="fpSection">
        <div className="fpContainer">
          <div className="fpHeader">
            <div className="fpHeaderLeft">
              <div className="fpTitleRow">
                <div className="fpTitle">Featured Projects</div>

                <div className="fpServerBadge">
                  <img
                    src={selectedServer === "india" ? indiaIcon : usIcon}
                    alt=""
                    className="fpServerBadgeIcon"
                  />
                  <span className="fpServerBadgeText">
                    {selectedServer === "india" ? "India Server" : "US Server"}
                  </span>
                </div>
              </div>

              <div className="fpSub">
                Explore our projects showcasing tech-enabled interior design expertise
              </div>
            </div>
          </div>

          <div className="fpControls">
            <div className="dv-chips fpChips">
              {typeOptions.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`dv-chip ${t === activeCategory2 ? "dv-chip--active" : ""}`}
                  onClick={() => setActiveCategory2(t)}
                >
                  {t}
                </button>
              ))}
            </div>

            <div className="fpSearchWrap">
              <img
                src={searchIcon}
                alt="Search"
                className="fpSearchIconImg"
                draggable={false}
              />
              <input
                value={searchQuery2}
                onChange={(e) => setSearchQuery2(e.target.value)}
                placeholder="Search Projects.."
                className="fpSearchInput"
              />
            </div>
          </div>

          {loading ? (
            <div className="fpEmpty">Curating projects for you…</div>
          ) : (
            <>
              <div className="fpGrid">
                {filtered.map((item, idx) => (
                  <FeaturedCard
                    key={item.id}
                    item={item}
                    onOpenProject={handleOpenProject}
                    onOpenVizdom={handleOpenVizdom}
                    onOpenYoutube={handleOpenYoutube}
                    onOpenDemo={handleOpenDemo}
                  />
                ))}
              </div>

              {filtered.length === 0 && (
                <div className="fpEmpty">No projects found matching your criteria.</div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

const sx = {
  page: {
    minHeight: "100vh",
    background: "#ffffff",
    color: "#141414",
    fontFamily: "var(--font-sans)",
  },
};